import type { Moment } from 'moment';
import type { ReactNode } from 'react';

export interface IEventTimelineItem {
    key: string;
    year: number;
    month: number;
    row: number;
    content: ReactNode;
}

export const COLUMN_WIDTH = 96;

// Vertical rhythm: each row drops roughly one card height plus a gap so close events don't overlap.
const ROW_HEIGHT = 144;
const ROW_GAP = 24;
const CARD_HEIGHT = 144;
const BOTTOM_PADDING = 48;

// Vertical offset (px) of a card in the given row.
export const rowDistance = (row: number) => (row - 1) * ROW_HEIGHT + row * ROW_GAP;

// Horizontal offset (px) of a year's boundary line, accounting for the leading column with one empty leading column so the first year's centered.
export const yearLeft = (year: number, startYear: number) => (year - startYear + 1) * COLUMN_WIDTH;

// Horizontal offset (px) of an event, placed by month within its year column.
export const itemLeft = (year: number, month: number, startYear: number) => yearLeft(year, startYear) + (month / 12) * COLUMN_WIDTH;

// Total panel height (px) needed to fit the deepest stacking row.
export const panelHeight = (items: IEventTimelineItem[]) => {
    const maxRow = items.reduce((max, item) => Math.max(max, item.row), 0);

    return rowDistance(maxRow) + CARD_HEIGHT + BOTTOM_PADDING;
};

// Horizontal offset (px) of the exact-date today line, or `undefined` when today is out of range.
export const todayLeft = (startYear: number, endYear: number, now: Moment): number | undefined => {
    const year = now.year();
    if (year < startYear || year > endYear) {
        return undefined;
    }
    const daysInYear = now.isLeapYear() ? 366 : 365;

    return yearLeft(year, startYear) + ((now.dayOfYear() - 1) / daysInYear) * COLUMN_WIDTH;
};
